import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Lightbulb, TrendingDown, TrendingUp } from 'lucide-react';
import { MoneyDisplay } from '../MoneyDisplay';
import { useCashFlowProjection, WeeklyDetails } from '@/hooks/finance/useCashFlowData';
import type { BusinessUnit } from '../BusinessUnitSelector';
import type { Brand } from '../BrandSelector';
import { cn } from '@/lib/utils';
import { CashFlowDetailsModal } from './CashFlowDetailsModal';

type DetailCategory = 'aVencer' | 'atraso' | 'novasVendas' | 'folha' | 'fixas' | 'impostos' | 'saldoInicial' | 'saldoFinal';

interface CashFlowProjectedProps {
  businessUnit?: BusinessUnit;
  brand?: Brand;
  weeks?: number;
}

interface SelectedCell {
  weekIndex: number;
  category: DetailCategory;
  weekLabel: string;
  value: number;
  details: WeeklyDetails;
}

export function CashFlowProjected({ 
  businessUnit = 'all', 
  brand = 'all',
  weeks = 4 
}: CashFlowProjectedProps) {
  const { data, isLoading } = useCashFlowProjection(businessUnit, brand, weeks);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

  const handleCellClick = (weekIndex: number, category: DetailCategory, weekLabel: string, value: number, details: WeeklyDetails) => {
    setSelectedCell({ weekIndex, category, weekLabel, value, details });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const clickableClass = "cursor-pointer hover:bg-muted/50 transition-colors";

  return (
    <div className="space-y-6">
      {/* Projeção Semanal */}
      <Card>
        <CardHeader>
          <CardTitle>Projeção Semanal</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">💡 Clique nos valores para ver detalhes</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium"></th>
                  {data?.weeks.map((week, i) => (
                    <th key={i} className="text-right py-2 font-medium px-3">
                      {week.weekLabel}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b bg-muted/30">
                  <td className="py-2 font-medium">Saldo Inicial</td>
                  {data?.weeks.map((week, i) => (
                    <td key={i} className="text-right py-2 px-3">
                      <MoneyDisplay value={week.saldoInicial} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>

                {/* Entradas */}
                <tr className="border-b">
                  <td className="py-2 font-semibold text-emerald-600" colSpan={weeks + 1}>
                    ENTRADAS
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">A Vencer (curva)</td>
                  {data?.weeks.map((week, i) => (
                    <td 
                      key={i} 
                      className={cn("text-right py-1 px-3 text-emerald-600", clickableClass)}
                      onClick={() => handleCellClick(i, 'aVencer', week.weekLabel, week.entradasAVencer, week.details)}
                    >
                      <MoneyDisplay value={week.entradasAVencer} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">Em Atraso (40%)</td>
                  {data?.weeks.map((week, i) => (
                    <td 
                      key={i} 
                      className={cn("text-right py-1 px-3 text-emerald-600/70", clickableClass)}
                      onClick={() => handleCellClick(i, 'atraso', week.weekLabel, week.entradasAtraso40, week.details)}
                    >
                      <MoneyDisplay value={week.entradasAtraso40} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">
                    Vendas Novas
                    {data?.weeks[0]?.usandoDreProjetado && (
                      <span className="ml-1 text-xs text-emerald-500">(DRE)</span>
                    )}
                  </td>
                  {data?.weeks.map((week, i) => (
                    <td 
                      key={i} 
                      className={cn("text-right py-1 px-3 text-emerald-600/70", clickableClass)}
                      onClick={() => handleCellClick(i, 'novasVendas', week.weekLabel, week.entradasNovasVendas, week.details)}
                    >
                      <MoneyDisplay value={week.entradasNovasVendas} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>
                <tr className="border-b bg-emerald-50 dark:bg-emerald-950/20">
                  <td className="py-2 font-medium">Total Entradas</td>
                  {data?.weeks.map((week, i) => (
                    <td key={i} className="text-right py-2 px-3 font-medium text-emerald-600">
                      <MoneyDisplay value={week.totalEntradas} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>

                {/* Saídas */}
                <tr className="border-b">
                  <td className="py-2 font-semibold text-destructive" colSpan={weeks + 1}>
                    SAÍDAS
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">Folha</td>
                  {data?.weeks.map((week, i) => (
                    <td 
                      key={i} 
                      className={cn("text-right py-1 px-3 text-destructive", week.saidasFolha > 0 && clickableClass)}
                      onClick={() => week.saidasFolha > 0 && handleCellClick(i, 'folha', week.weekLabel, week.saidasFolha, week.details)}
                    >
                      {week.saidasFolha > 0 ? (
                        <MoneyDisplay value={week.saidasFolha} showCents={false} size="sm" />
                      ) : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">Despesas Fixas</td>
                  {data?.weeks.map((week, i) => (
                    <td 
                      key={i} 
                      className={cn("text-right py-1 px-3 text-destructive", clickableClass)}
                      onClick={() => handleCellClick(i, 'fixas', week.weekLabel, week.saidasFixas, week.details)}
                    >
                      <MoneyDisplay value={week.saidasFixas} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">Fornecedores</td>
                  {data?.weeks.map((week, i) => (
                    <td key={i} className="text-right py-1 px-3 text-destructive">
                      <MoneyDisplay value={week.saidasFornecedores} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1 pl-4 text-muted-foreground">Impostos</td>
                  {data?.weeks.map((week, i) => (
                    <td 
                      key={i} 
                      className={cn(
                        "text-right py-1 px-3",
                        week.saidasImpostos > 20000 ? "text-amber-600 font-medium" : "text-destructive",
                        week.saidasImpostos > 0 && clickableClass
                      )}
                      onClick={() => week.saidasImpostos > 0 && handleCellClick(i, 'impostos', week.weekLabel, week.saidasImpostos, week.details)}
                    >
                      {week.saidasImpostos > 0 ? (
                        <MoneyDisplay value={week.saidasImpostos} showCents={false} size="sm" />
                      ) : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b bg-red-50 dark:bg-red-950/20">
                  <td className="py-2 font-medium">Total Saídas</td>
                  {data?.weeks.map((week, i) => (
                    <td key={i} className="text-right py-2 px-3 font-medium text-destructive">
                      <MoneyDisplay value={week.totalSaidas} showCents={false} size="sm" />
                    </td>
                  ))}
                </tr>

                {/* Saldo Final */}
                <tr className="bg-muted/50">
                  <td className="py-3 font-bold">SALDO FINAL</td>
                  {data?.weeks.map((week, i) => {
                    const variacao = week.saldoFinal - week.saldoInicial;
                    const isPositive = variacao >= 0;
                    return (
                      <td key={i} className="text-right py-3 px-3">
                        <div className="font-bold">
                          <MoneyDisplay value={week.saldoFinal} showCents={false} size="sm" />
                        </div>
                        <div className={cn(
                          "text-xs flex items-center justify-end gap-1",
                          isPositive ? "text-emerald-600" : "text-destructive"
                        )}>
                          {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {isPositive ? '+' : ''}<MoneyDisplay value={variacao} showCents={false} size="sm" className="inline" />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Alertas */}
      {data?.alerts && data.alerts.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-600 flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4" />
              Alertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {data.alerts.map((alert, i) => (
                <li key={i} className={cn(
                  "flex items-start gap-2",
                  alert.type === 'danger' ? 'text-destructive' : 'text-amber-600'
                )}>
                  • {alert.message}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Recomendações */}
      {data?.recommendations && data.recommendations.length > 0 && (
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-blue-600 flex items-center gap-2 text-base">
              <Lightbulb className="h-4 w-4" />
              Recomendações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {data.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span>{rec.icon}</span>
                  <span>{rec.message}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Modal de Detalhes */}
      <CashFlowDetailsModal
        isOpen={selectedCell !== null}
        onClose={() => setSelectedCell(null)}
        weekLabel={selectedCell?.weekLabel || ''}
        weekIndex={selectedCell?.weekIndex || 0}
        category={selectedCell?.category || 'aVencer'}
        details={selectedCell?.details || null}
        value={selectedCell?.value || 0}
      />
    </div>
  );
}
